<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useProjectsStore } from '@/stores/projects';

const route = useRoute();
const { t } = useI18n();
const { current: currentProject } = storeToRefs(useProjectsStore());

interface Crumb {
  label: string;
  to?: string;
  active?: boolean;
}

/** Inside a project workspace the trail is Projects › {project name} › {submodule}. */
const inProjectWorkspace = computed(() => route.matched.some((r) => r.name === 'project-workspace'));

const crumbs = computed<Crumb[]>(() => {
  const out: Crumb[] = [];

  if (inProjectWorkspace.value) {
    out.push({ label: t('projects'), to: '/projects' });
    const projectName = currentProject.value?.name || t('project');
    const submoduleKey = route.meta?.submodule as string | undefined;
    const projectTo = `/projects/${String(route.params.id ?? '')}`;
    if (submoduleKey) {
      out.push({ label: projectName, to: projectTo });
      out.push({ label: t(submoduleKey), active: true });
    } else {
      out.push({ label: projectName, active: true });
    }
    return out;
  }

  const parentKey = (route.meta?.parent as string | undefined) ?? null;
  if (parentKey) {
    out.push({ label: t(parentKey), to: `/${parentKey}` });
  }
  const labelKey = (route.meta?.breadcrumb as string | undefined) ?? route.name?.toString() ?? '';
  if (labelKey) {
    out.push({ label: t(labelKey), active: true });
  }
  return out;
});
</script>

<template>
  <nav class="qb-breadcrumb" :aria-label="t('breadcrumb') || 'Breadcrumb'">
    <ol class="qb-breadcrumb__list">
      <li class="qb-breadcrumb__item qb-breadcrumb__item--home">
        <router-link to="/" class="qb-breadcrumb__home" :aria-label="t('home') || 'Home'">
          <i class="pi pi-home" aria-hidden="true" />
        </router-link>
      </li>
      <template v-for="(crumb, idx) in crumbs" :key="idx">
        <li class="qb-breadcrumb__sep" aria-hidden="true">
          <i class="pi pi-chevron-right qb-breadcrumb__sep-icon" />
        </li>
        <li class="qb-breadcrumb__item">
          <router-link
            v-if="crumb.to && !crumb.active"
            :to="crumb.to"
            class="qb-breadcrumb__link"
          >
            {{ crumb.label }}
          </router-link>
          <span v-else class="qb-breadcrumb__current" aria-current="page">
            {{ crumb.label }}
          </span>
        </li>
      </template>
    </ol>
  </nav>
</template>

<style scoped>
.qb-breadcrumb {
  display: flex;
  align-items: center;
  height: 45px;
  padding: 0 14px;
  background: var(--qb-surface-0);
  border: 1px solid var(--qb-surface-border);
  border-radius: var(--qb-radius-md);
}

.qb-breadcrumb__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--qb-font-size-sm);
  line-height: 1;
}

.qb-breadcrumb__item {
  display: inline-flex;
  align-items: center;
}

.qb-breadcrumb__home {
  display: inline-flex;
  align-items: center;
  color: var(--qb-text-subtle);
  font-size: 16px;
  text-decoration: none;
}

.qb-breadcrumb__home:hover {
  color: var(--qb-color-primary);
}

.qb-breadcrumb__sep {
  display: inline-flex;
  align-items: center;
  color: var(--qb-text-muted);
}

.qb-breadcrumb__sep-icon {
  font-size: 10px;
}

[dir='rtl'] .qb-breadcrumb__sep-icon {
  transform: scaleX(-1);
}

.qb-breadcrumb__link {
  color: var(--qb-text-muted);
  font-weight: var(--qb-font-weight-medium);
  text-decoration: none;
}

.qb-breadcrumb__link:hover {
  color: var(--qb-color-primary);
  text-decoration: none;
}

.qb-breadcrumb__current {
  color: var(--qb-color-primary);
  font-weight: var(--qb-font-weight-medium);
}
</style>
